import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowRight,
  IconCalculator,
  IconFileText,
  IconRefresh,
  IconSchool,
} from "@tabler/icons-react";
import { ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createFaqSchema,
} from "../../seo/utils";
import "./FinalGradeCalculatorPage.css";

type NumberValue = number | "";
type FinalGradeMode = "required" | "overall";

const TOOL_PATH = "/tools/final-grade-calculator";
const TOOLS_PATH = "/tools";

const faqs = [
  {
    question: "How do I calculate my final grade?",
    answer:
      "To calculate your final grade, multiply your current grade by the percentage of the course already completed, then add your final exam grade multiplied by the final exam weight.",
  },
  {
    question: "What grade do I need on my final exam?",
    answer:
      "You can find this by entering your current grade, desired final grade, and final exam weight into the final grade calculator.",
  },
  {
    question: "How do I calculate final grade with weighted percentages?",
    answer:
      "Use the weighted formula: final grade equals current grade multiplied by its remaining weight plus final exam grade multiplied by the final exam weight.",
  },
  {
    question: "What if I need more than 100% on my final?",
    answer:
      "If the calculator shows that you need more than 100%, your target grade may not be possible unless your course offers extra credit.",
  },
  {
    question: "Is this final grade calculator accurate?",
    answer:
      "The calculator is accurate based on the numbers entered. However, grading policies can vary, so always confirm your course grading rules.",
  },
];

const finalExamRows = [
  ["80%", "85%", "30%", "96.67%"],
  ["85%", "90%", "25%", "105%"],
  ["75%", "80%", "40%", "87.5%"],
  ["90%", "93%", "20%", "105%"],
];

export function calculateRequiredFinalGrade(
  currentGrade: NumberValue,
  desiredGrade: NumberValue,
  finalWeight: NumberValue,
) {
  const current = Number(currentGrade);
  const desired = Number(desiredGrade);
  const weight = Number(finalWeight) / 100;

  if (Number.isNaN(current) || Number.isNaN(desired) || Number.isNaN(weight)) {
    return null;
  }

  if (current < 0 || desired < 0 || weight <= 0 || weight > 1) return null;

  return (desired - current * (1 - weight)) / weight;
}

export function calculateOverallFinalGrade(
  currentGrade: NumberValue,
  finalExamGrade: NumberValue,
  finalWeight: NumberValue,
) {
  const current = Number(currentGrade);
  const exam = Number(finalExamGrade);
  const weight = Number(finalWeight) / 100;

  if (Number.isNaN(current) || Number.isNaN(exam) || Number.isNaN(weight)) {
    return null;
  }

  if (current < 0 || exam < 0 || weight < 0 || weight > 1) return null;

  return current * (1 - weight) + exam * weight;
}

export function roundGrade(value: number) {
  return Math.round(value * 100) / 100;
}

function toNumberInputValue(value: string | number): NumberValue {
  if (value === "") {
    return "";
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : "";
}

function numberValue(value: NumberValue) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatGrade(value: number) {
  const rounded = roundGrade(value);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function FinalGradeCalculatorPage() {
  const webApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Final Grade Calculator",
    applicationCategory: "EducationalApplication",
    operatingSystem: "All",
    url: buildCanonicalUrl(TOOL_PATH),
    description:
      "Free final grade calculator to find what score you need on your final exam to reach your desired class grade.",
    isAccessibleForFree: true,
  };

  return (
    <Container fluid className="final-grade-page" px={0}>
      <Seo
        title="Final Grade Calculator | What Grade Do I Need on My Final?"
        description="Use our free Final Grade Calculator to find what score you need on your final exam to reach your desired class grade. Calculate final exam grade instantly."
        path={TOOL_PATH}
        ogTitle="Final Grade Calculator"
        ogDescription="Find what score you need on your final exam to reach your desired course grade."
        keywords={[
          "final grade calculator",
          "final exam calculator",
          "final exam grade calculator",
          "finals calculator",
          "grade calculator final",
          "how to calculate final grade",
        ]}
        schema={[
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: TOOLS_PATH },
            { name: "Final Grade Calculator", path: TOOL_PATH },
          ]),
          createFaqSchema(faqs),
          webApplicationSchema,
        ]}
      />

      <Box className="final-grade-hero">
        <Container size="xl">
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to={TOOLS_PATH}>
                Tools
              </Anchor>
              <Text>Final Grade Calculator</Text>
            </Breadcrumbs>

            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={34}>
              <Stack gap="md" justify="center">
                <Group gap="sm">
                  <Badge color="green" radius="sm" size="lg">
                    Grade & GPA Tools
                  </Badge>
                  <Badge color="blue" radius="sm" size="lg" variant="light">
                    Finals calculator
                  </Badge>
                </Group>
                <ThemeIcon size={58} radius="md" color="green" variant="light">
                  <IconCalculator size={32} />
                </ThemeIcon>
                <Title order={1} className="final-grade-title">
                  Final Grade Calculator
                </Title>
                <Stack gap="xs" className="final-grade-intro">
                  <Text size="lg" c="dimmed">
                    Use our free Final Grade Calculator to find out what score
                    you need on your final exam to reach your desired course
                    grade. Enter your current grade, target grade, and final
                    exam weight to calculate the final exam score you need.
                  </Text>
                  <Text c="dimmed">
                    This tool is useful for students preparing for finals,
                    exams, coursework, and weighted grading systems.
                  </Text>
                </Stack>
              </Stack>

              <Paper
                withBorder
                radius="md"
                p={{ base: "md", md: "xl" }}
                className="final-grade-panel"
              >
                <FinalGradeCalculator />
              </Paper>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={48}>
        <main className="final-grade-content">
          <FinalGradeSeoContent />
          <FinalGradeCta />
          <RelatedFinalGradeTools />
          <FinalGradeFAQ />
        </main>
      </Container>
    </Container>
  );
}

export function FinalGradeCalculator() {
  const [mode, setMode] = useState<FinalGradeMode>("required");

  return (
    <Stack gap="lg" component="section" aria-label="Final grade calculator">
      <Stack gap={6}>
        <Title order={2}>Calculate Final Grade</Title>
        <Text c="dimmed">
          Find what score you need on your final, or calculate your final class
          grade after entering your final exam score.
        </Text>
      </Stack>

      <SegmentedControl
        fullWidth
        value={mode}
        onChange={(value) => setMode(value as FinalGradeMode)}
        className="final-grade-mode-control"
        data={[
          { value: "required", label: "Final Exam Grade Needed" },
          { value: "overall", label: "Calculate Overall Final Grade" },
        ]}
        aria-label="Final grade calculator mode"
      />

      {mode === "required" ? <RequiredFinalGradeCalculator /> : null}
      {mode === "overall" ? <OverallFinalGradeCalculator /> : null}
    </Stack>
  );
}

function RequiredFinalGradeCalculator() {
  const [currentGrade, setCurrentGrade] = useState<NumberValue>(82);
  const [desiredGrade, setDesiredGrade] = useState<NumberValue>(90);
  const [finalWeight, setFinalWeight] = useState<NumberValue>(30);
  const errors = [
    currentGrade === "" ? "Current grade is required." : "",
    desiredGrade === "" ? "Desired final course grade is required." : "",
    finalWeight === "" ? "Final exam weight is required." : "",
    numberValue(currentGrade) < 0 ? "Current grade cannot be negative." : "",
    numberValue(desiredGrade) < 0 ? "Desired final grade cannot be negative." : "",
    numberValue(finalWeight) <= 0 || numberValue(finalWeight) > 100
      ? "Please enter a valid final exam weight greater than 0."
      : "",
  ].filter(Boolean);
  const requiredGrade = !errors.length
    ? calculateRequiredFinalGrade(currentGrade, desiredGrade, finalWeight)
    : null;

  return (
    <Stack gap="lg">
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <GradeInput
          label="Current grade in the class"
          placeholder="e.g. 82"
          value={currentGrade}
          onChange={setCurrentGrade}
        />
        <GradeInput
          label="Desired final course grade"
          placeholder="e.g. 90"
          value={desiredGrade}
          onChange={setDesiredGrade}
        />
        <GradeInput
          label="Final exam weight percentage"
          placeholder="e.g. 30"
          value={finalWeight}
          onChange={setFinalWeight}
          max={100}
        />
      </SimpleGrid>

      <ResetButton
        onReset={() => {
          setCurrentGrade(82);
          setDesiredGrade(90);
          setFinalWeight(30);
        }}
      />

      <ValidationAlerts errors={errors} />

      {requiredGrade !== null ? (
        <FinalGradeResult title="Final Exam Grade Needed">
          {requiredGrade < 0 ? (
            <Text>
              You have already secured your desired grade based on the values
              entered.
            </Text>
          ) : (
            <Text>
              You need <strong>{formatGrade(requiredGrade)}%</strong> on your
              final exam to finish with{" "}
              <strong>{formatGrade(numberValue(desiredGrade))}%</strong> in the
              class.
            </Text>
          )}
          {requiredGrade > 100 ? (
            <Text c="orange" fw={700}>
              You would need more than 100% on the final exam. Your desired
              final grade may not be possible unless extra credit is available.
            </Text>
          ) : null}
        </FinalGradeResult>
      ) : null}
    </Stack>
  );
}

function OverallFinalGradeCalculator() {
  const [currentGrade, setCurrentGrade] = useState<NumberValue>(84);
  const [finalExamGrade, setFinalExamGrade] = useState<NumberValue>(88);
  const [finalWeight, setFinalWeight] = useState<NumberValue>(30);
  const errors = [
    currentGrade === "" ? "Current grade is required." : "",
    finalExamGrade === "" ? "Final exam grade is required." : "",
    finalWeight === "" ? "Final exam weight is required." : "",
    numberValue(currentGrade) < 0 ? "Current grade cannot be negative." : "",
    numberValue(finalExamGrade) < 0 ? "Final exam grade cannot be negative." : "",
    numberValue(finalWeight) < 0 || numberValue(finalWeight) > 100
      ? "Final exam weight must be between 0 and 100."
      : "",
  ].filter(Boolean);
  const overallGrade = !errors.length
    ? calculateOverallFinalGrade(currentGrade, finalExamGrade, finalWeight)
    : null;

  return (
    <Stack gap="lg">
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <GradeInput
          label="Current grade"
          placeholder="e.g. 82"
          value={currentGrade}
          onChange={setCurrentGrade}
        />
        <GradeInput
          label="Final exam grade"
          placeholder="e.g. 90"
          value={finalExamGrade}
          onChange={setFinalExamGrade}
        />
        <GradeInput
          label="Final exam weight"
          placeholder="e.g. 30"
          value={finalWeight}
          onChange={setFinalWeight}
          max={100}
        />
      </SimpleGrid>

      <ResetButton
        onReset={() => {
          setCurrentGrade(84);
          setFinalExamGrade(88);
          setFinalWeight(30);
        }}
      />

      <ValidationAlerts errors={errors} />

      {overallGrade !== null ? (
        <FinalGradeResult title="Your Overall Final Grade">
          <Text>
            Your final class grade will be approximately{" "}
            <strong>{formatGrade(overallGrade)}%</strong>.
          </Text>
        </FinalGradeResult>
      ) : null}
    </Stack>
  );
}

function GradeInput({
  label,
  placeholder,
  value,
  onChange,
  max,
}: {
  label: string;
  placeholder: string;
  value: NumberValue;
  onChange: (value: NumberValue) => void;
  max?: number;
}) {
  return (
    <NumberInput
      label={label}
      placeholder={placeholder}
      value={value}
      min={0}
      max={max}
      allowNegative={false}
      rightSection={<Text size="xs">%</Text>}
      onChange={(nextValue) => onChange(toNumberInputValue(nextValue))}
      aria-label={label}
    />
  );
}

function ResetButton({ onReset }: { onReset: () => void }) {
  return (
    <Group>
      <Button
        variant="outline"
        color="gray"
        leftSection={<IconRefresh size={18} />}
        onClick={onReset}
      >
        Reset Calculator
      </Button>
    </Group>
  );
}

function ValidationAlerts({ errors }: { errors: string[] }) {
  if (!errors.length) {
    return null;
  }

  return (
    <Alert icon={<IconAlertCircle size={18} />} color="red" radius="md">
      <ul className="final-grade-alert-list">
        {errors.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    </Alert>
  );
}

function FinalGradeResult({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Box className="final-grade-result" aria-live="polite">
      <Stack gap="sm">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </Box>
  );
}

function FinalGradeSeoContent() {
  return (
    <>
      <SeoTextSection title="How to Use the Final Grade Calculator">
        <Text>
          Enter your current grade, desired final grade, and the weight of your
          final exam. The calculator will show the score you need on your final
          exam to reach your target grade.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How to Calculate Final Grade">
        <Text>
          To calculate your final grade, multiply your current grade by the
          portion of the course already completed, then add your final exam
          score multiplied by the final exam weight.
        </Text>
        <FormulaBox>
          Final Grade = Current Grade x (1 - Final Weight) + Final Exam Grade x
          Final Weight
        </FormulaBox>
      </SeoTextSection>

      <SeoTextSection title="How to Calculate What You Need on Your Final Exam">
        <Text>
          To find the score you need on your final exam, subtract your weighted
          current grade from your desired grade, then divide by the final exam
          weight.
        </Text>
        <FormulaBox>
          Required Final Exam Grade = (Desired Grade - Current Grade x (1 -
          Final Weight)) / Final Weight
        </FormulaBox>
      </SeoTextSection>

      <SeoTextSection title="Final Grade Calculator Example">
        <Text>
          If your current grade is 85%, your desired grade is 90%, and your
          final exam is worth 30%, you need about 101.67% on the final exam.
          This means your target may not be possible unless extra credit is
          available.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="When Should Students Use This Calculator?">
        <Text>
          Students can use this final grade calculator before exams, during
          finals week, or while planning study goals. It helps you understand
          whether your target grade is realistic and how much effort may be
          needed on the final exam.
        </Text>
      </SeoTextSection>

      <EstimateTable />
    </>
  );
}

function SeoTextSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="final-grade-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </section>
  );
}

function FormulaBox({ children }: { children: ReactNode }) {
  return <Box className="final-grade-formula formula-box">{children}</Box>;
}

function EstimateTable() {
  return (
    <section className="final-grade-section">
      <Stack gap="md">
        <Title order={2}>Example Final Exam Grade Needed</Title>
        <Box className="final-grade-table-wrap">
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Current Grade</Table.Th>
                <Table.Th>Desired Grade</Table.Th>
                <Table.Th>Final Weight</Table.Th>
                <Table.Th>Score Needed on Final</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {finalExamRows.map((row) => (
                <Table.Tr key={row.join("-")}>
                  {row.map((cell) => (
                    <Table.Td key={cell}>{cell}</Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </Stack>
    </section>
  );
}

function FinalGradeCta() {
  return (
    <section className="final-grade-cta cta-box">
      <Group justify="space-between" align="center" gap="lg">
        <Group gap="md" align="flex-start" wrap="nowrap">
          <ThemeIcon radius="md" size={54} color="blue">
            <IconSchool size={28} />
          </ThemeIcon>
          <Stack gap={4}>
            <Title order={2}>Need Help Before Your Final Exam?</Title>
            <Text c="dimmed" className="final-grade-cta-copy">
              If your required final exam score is high, SubjectBuddy can
              connect you with academic support experts for assignment help,
              exam preparation, and study guidance.
            </Text>
          </Stack>
        </Group>
        <Button
          component={Link}
          to="/assignment-help"
          radius="md"
          className="cta-button"
          rightSection={<IconArrowRight size={18} />}
        >
          Get Academic Help
        </Button>
      </Group>
    </section>
  );
}

function RelatedFinalGradeTools() {
  const relatedItems = [
    {
      title: "Grade Calculator",
      href: "/tools/grade-calculator",
      description: "Calculate current, weighted, test, and final grade scenarios.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "GPA Calculator",
      href: "/tools/gpa-calculator",
      description: "Estimate semester, cumulative, college, and high school GPA.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Test Grade Calculator",
      href: "/tools/test-grade-calculator",
      description: "Calculate test percentage from correct or wrong answers.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "Weighted Grade Calculator",
      href: "/tools/weighted-grade-calculator",
      description: "Use weighted categories to estimate class grades.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Assignment Help",
      href: "/assignment-help",
      description: "Get help improving coursework before final grades are set.",
      icon: <IconSchool size={20} />,
    },
    {
      title: "Homework Help",
      href: "/homework-help",
      description: "Keep coursework from pulling down your final grade.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "Exam Help",
      href: "/exam-help",
      description: "Prepare for the exam score your target grade needs.",
      icon: <IconSchool size={20} />,
    },
    {
      title: "Study Help",
      href: "/study-help",
      description: "Plan focused study support before finals week.",
      icon: <IconFileText size={20} />,
    },
  ];

  return (
    <section className="final-grade-section">
      <Stack gap="md">
        <Title order={2}>Related Student Tools</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {relatedItems.map((item) => (
            <Paper
              key={item.href}
              component={Link}
              to={item.href}
              withBorder
              radius="md"
              p="lg"
              className="final-grade-link-card"
            >
              <Group gap="md" wrap="nowrap" align="flex-start">
                <ThemeIcon color="green" variant="light" radius="md">
                  {item.icon}
                </ThemeIcon>
                <Stack gap={4}>
                  <Text fw={800}>{item.title}</Text>
                  <Text size="sm" c="dimmed">
                    {item.description}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
    </section>
  );
}

function FinalGradeFAQ() {
  return (
    <section className="final-grade-section">
      <Stack gap="md">
        <Title order={2}>FAQs About Final Grade Calculator</Title>
        {faqs.map((faq) => (
          <Paper key={faq.question} withBorder radius="md" p="lg">
            <Stack gap={6}>
              <Title order={3}>{faq.question}</Title>
              <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                {faq.answer}
              </Text>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </section>
  );
}

export default FinalGradeCalculatorPage;
